import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import CONFIG from '../../config';

// Admin UI for managing Data Subject Rights (access, rectification, erasure, consent withdrawal, breach notification)
const DataSubjectRightsAdmin = () => {
    const { t } = useTranslation();
    const token = localStorage.getItem('token');

    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [personalData, setPersonalData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ username: '', email: '', fullname: '', phone: '' });

    // Fetch all users (admin endpoint)
    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${CONFIG.API_URL}/api/admin/users`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const usersData = Array.isArray(res.data)
                ? res.data
                : Array.isArray(res.data?.users)
                    ? res.data.users
                    : Array.isArray(res.data?.data)
                        ? res.data.data
                        : [];
            setUsers(usersData);
        } catch (e) {
            console.error('Error fetching users:', e);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const loadPersonalData = async (userId) => {
        try {
            const res = await axios.get(`${CONFIG.API_URL}/api/admin/data-subject/users/${userId}/personal-data`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setPersonalData(res.data);
            setForm({
                username: res.data.username || '',
                email: res.data.email || '',
                fullname: res.data.fullname || '',
                phone: res.data.phone || '',
            });
            setSelectedUser(userId);
        } catch (e) {
            console.error('Error loading personal data:', e);
        }
    };

    const handleUpdate = async () => {
        if (!selectedUser) return;
        try {
            await axios.put(
                `${CONFIG.API_URL}/api/admin/data-subject/users/${selectedUser}/personal-data`,
                form,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert(t('admin.update_success', 'Personal data updated'));
            loadPersonalData(selectedUser);
        } catch (e) {
            console.error('Error updating personal data:', e);
        }
    };

    const handleErase = async () => {
        if (!selectedUser) return;
        if (!window.confirm(t('admin.confirm_erase', 'Erase all personal data for this user?')))
            return;
        try {
            await axios.delete(
                `${CONFIG.API_URL}/api/admin/data-subject/users/${selectedUser}/personal-data`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert(t('admin.erase_success', 'Personal data erased'));
            setPersonalData(null);
            setSelectedUser(null);
            fetchUsers();
        } catch (e) {
            console.error('Error erasing personal data:', e);
        }
    };

    const handleWithdrawConsent = async () => {
        if (!selectedUser) return;
        try {
            await axios.post(
                `${CONFIG.API_URL}/api/admin/data-subject/users/${selectedUser}/consent/withdraw`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert(t('admin.consent_withdrawn', 'Consent withdrawn'));
        } catch (e) {
            console.error('Error withdrawing consent:', e);
        }
    };

    const handleNotifyBreach = async () => {
        if (!selectedUser) return;
        const breachId = prompt(t('admin.enter_breach_id', 'Enter breach ID to notify'));
        const description = prompt(t('admin.enter_description', 'Enter description for the breach'));
        if (!breachId) return;
        try {
            await axios.post(
                `${CONFIG.API_URL}/api/admin/data-subject/users/${selectedUser}/breach-notify`,
                { breachId, description },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert(t('admin.breach_notified', 'User notified of breach'));
        } catch (e) {
            console.error('Error notifying breach:', e);
        }
    };

    return (
        <div className="p-6">
            <h1 className="text-3xl font-bold mb-4">{t('admin.data_subject_rights', 'Data Subject Rights')}</h1>
            <div className="grid grid-cols-2 gap-4">
                {/* Users list */}
                <div className="border p-4 overflow-y-auto max-h-96">
                    <h2 className="font-semibold mb-2">{t('admin.users', 'Users')}</h2>
                    {loading ? (
                        <p>{t('admin.loading', 'Loading...')}</p>
                    ) : (
                        <ul>
                            {users.map((u) => (
                                <li key={u.id} className="cursor-pointer hover:bg-gray-100 p-1" onClick={() => loadPersonalData(u.id)}>
                                    {u.username} ({u.email})
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                {/* Personal data & actions */}
                {selectedUser && personalData && (
                    <div className="border p-4">
                        <h2 className="font-semibold mb-2">{t('admin.personal_data', 'Personal Data')}</h2>
                        <div className="grid gap-2">
                            <label>{t('admin.username', 'Username')}</label>
                            <input className="border p-1" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
                            <label>{t('admin.email', 'Email')}</label>
                            <input className="border p-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                            <label>{t('admin.fullname', 'Full Name')}</label>
                            <input className="border p-1" value={form.fullname} onChange={(e) => setForm({ ...form, fullname: e.target.value })} />
                            <label>{t('admin.phone', 'Phone')}</label>
                            <input className="border p-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        </div>
                        <div className="mt-4 flex space-x-2">
                            <button className="bg-blue-600 text-white px-3 py-1" onClick={handleUpdate}>
                                {t('admin.update', 'Update')}
                            </button>
                            <button className="bg-red-600 text-white px-3 py-1" onClick={handleErase}>
                                {t('admin.erase', 'Erase')}
                            </button>
                        </div>
                        <div className="mt-4 flex space-x-2">
                            <button className="bg-yellow-600 text-white px-3 py-1" onClick={handleWithdrawConsent}>
                                {t('admin.withdraw_consent', 'Withdraw Consent')}
                            </button>
                            <button className="bg-purple-600 text-white px-3 py-1" onClick={handleNotifyBreach}>
                                {t('admin.notify_breach', 'Notify Breach')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DataSubjectRightsAdmin;
